import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ClipboardList, X } from 'lucide-react';
import { performEnterpriseGPTApiCall } from '../../Hooks/useMiddlewareApiCall';
import { Survey } from '../../EnterpriseGPTApi/EnterpriseGPTApiV1Client';
import { useNotification } from '../../Contexts/NotificationContext';
import SurveyView from './SurveyView';
import SyButton from '../Common/SyButton';
import { HorizontalSeparatorLine } from '../Common/HorizontalSeparatorLine';
import '../../styles/skeletons.css';

export interface SurveyViewPageProps {
    surveyId: string;
    onClose: () => void;
}

const SurveyViewPage: FC<SurveyViewPageProps> = ({ surveyId, onClose }) => {
    const { t } = useTranslation();
    const { dispatchNotification } = useNotification();
    const [survey, setSurvey] = useState<Survey | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        const fetchSurvey = async () => {
            setIsLoading(true);
            await performEnterpriseGPTApiCall(async (client, signal) => {
                try {
                    const surveyData = await client.getSurvey(surveyId, signal);
                    setSurvey(surveyData);
                } catch (error) {
                    console.error('Failed to fetch survey:', error);
                    dispatchNotification(t('adminSettings.surveys.fetchError', 'Failed to fetch survey details'), 'error');
                } finally {
                    setIsLoading(false);
                }
            });
        };

        fetchSurvey();
    }, [surveyId, t, dispatchNotification]);

    if (isLoading) {
        return (
            <div className="settings-skeleton-container">
                <div className="react-loading-skeleton settings-skeleton-card h-460" />
            </div>
        );
    }

    return (
        <div>
            <div className="titleSection">
                <div className="titleSection-content">
                    <ClipboardList size={20} className="titleSection-userIcon" />
                    <h1 className="settingsTitle">{survey?.title || t('adminSettings.surveys.viewTitle', 'Survey Details')}</h1>
                </div>
                <SyButton
                    appearance="secondary"
                    icon={<X size={14} />}
                    onClick={onClose}
                    className="button-margin-right compact-survey-button"
                >
                    {t('common.close', 'Close')}
                </SyButton>
            </div>

            <HorizontalSeparatorLine />


            <div style={{ padding: '20px' }}>
                {survey && <SurveyView surveyModel={survey} onAnswerChange={() => {}} />}
            </div>
        </div>
    );
};

export default SurveyViewPage;
